import React, { Dispatch, SetStateAction, useCallback, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import { IconButton } from "@atlaskit/button-next/new";
import InformationCircleIcon from "@atlaskit/icon/core/information-circle";
import Popup, { TriggerProps } from "@atlaskit/popup";
import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { TRACKING_EVENT_ACTION_SUBJECT_IDS } from "@/templates/ondemand/signup/confirmation/confirmation-page/src/views/user-seg-bxp-ui/constants.ts";
import { useIsMobile } from "@/utils/useIsMobile";

import { JiraTemplateChoice } from "../../../hooks";
import { PRODUCT_NAMES, isProductKey } from "../../../models/product";
import { UserSegmentationTeamTypes } from "../../../models/user-segmentation";
import { messages } from "../messages";
import { isServiceManagementVariation } from "../team-type-template-map";
import {
    productKeyAttributes,
    TemplateOptionMetadata
} from "../template-definitions";

interface CardProps {
    imageSrc: string;
    mobileImgSrc: string;
    title: string;
    subtext: string;
    templateType: JiraTemplateChoice;
    templateId: string;
    setSelectedTemplate: Dispatch<SetStateAction<JiraTemplateChoice>>;
    setSelectedTemplateId: Dispatch<SetStateAction<string | undefined>>;
    selectedCardTitle: string | undefined;
    setSelectedCardTitle: Dispatch<SetStateAction<string | undefined>>;
    teamType: UserSegmentationTeamTypes;
    templateMetadata?: TemplateOptionMetadata;
    setSelectedTemplateMetadata: Dispatch<
        SetStateAction<TemplateOptionMetadata | undefined>
    >;
    info?: {
        title: string;
        content: string;
    };
    productKey: string;
}

const cardBorderStyle = css({
    margin: "1px",
    padding: "0px",
    borderRadius: "var(--rounding, 8px)",
    // eslint-disable-next-line local/ensure-wac-token-usage
    border: "1px solid #C1C7D0",
    background: wacToken("color.neutral.light0"),
    overflow: "hidden",
    display: "flex",
    flexDirection: "row",
    maxWidth: "463px",
    alignItems: "flex-start",
    cursor: "pointer",
    ["&:hover"]: {
        boxShadow:
            // eslint-disable-next-line local/ensure-wac-token-usage
            "0px 1px 1px 0px rgba(9, 30, 66, 0.25), 0px 0px 1px 0px rgba(9, 30, 66, 0.31)"
    },
    [media.above.md]: {
        flexDirection: "column",
        justifyContent: "flex-start",
        maxWidth: "none",
        minHeight: "333px",
        flex: 1,
        alignSelf: "flex-start"
    }
});

const unselectedButtonStyle = css({
    borderColor: token("color.background.accent.gray.subtlest")
});

const selectedButtonStyle = css({
    border: `2px solid ${token("color.link")}`,
    margin: 0
});

const cardTitleStyle = css({
    fontFamily: wacToken("font.charlie.text"),
    color: token("color.text"),
    fontSize: "14px",
    fontWeight: 700,
    lineHeight: "20px" /* 125% */,
    margin: "0 0 5px 0",
    [media.above.md]: {
        fontSize: "20px",
        lineHeight: "28px"
    }
});

const titleContainerStyle = css({
    display: "flex",
    alignItems: "center",
    gap: token("space.100")
});

const poweredByContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    gap: token("space.100"),
    marginBlockStart: token("space.100")
});

const poweredByRowStyle = css({
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: token("space.100")
});

const poweredByTextStyle = css({
    fontSize: "12px",
    fontFamily: wacToken("font.charlie.text"),
    color: wacToken("color.primary.black"),
    fontWeight: 400,
    lineHeight: "16px",
    margin: 0,
    [media.above.md]: {
        fontSize: "14px",
        lineHeight: "20px"
    }
});

const poweredByIconStyle = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
});

const popupContentStyle = css({
    padding: token("space.100"),
    maxWidth: "280px"
});

const popupTitleStyle = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "20px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.100")
});

const popupTextStyle = css({
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "12px",
    fontWeight: 400,
    lineHeight: "16px",
    color: wacToken("color.primary.black")
});

const subtextStyle = css({
    fontSize: "12px",
    fontFamily: wacToken("font.charlie.text"),
    color: wacToken("color.primary.black"),
    fontWeight: 400,
    lineHeight: "20px" /* 143% */,
    margin: 0,
    [media.above.md]: {
        fontSize: "16px",
        lineHeight: "24px"
    }
});

const textContainerStyle = css({
    fontFamily: "var(--atlassian-recommended-system-fonts)",
    padding: token("space.200"),
    display: "flex",
    flexDirection: "column",
    textAlign: "start",
    [media.above.md]: {
        padding: `${token("space.300")} ${token("space.300")}`,
        flexGrow: 1
    }
});

const imgStyle = css({
    display: "none",
    [media.above.md]: {
        display: "block",
        width: "100%",
        height: "100%",
        flexBasis: "0"
    }
});

const mobileImgStyle = css({
    width: "80px",
    height: "100px",
    [media.above.md]: {
        display: "none"
    }
});

const desktopImageContainer = css({
    display: "none",
    [media.above.md]: {
        display: "block",
        maxHeight: "200px",
        minHeight: "174px"
    }
});

interface PoweredBySectionProps {
    productKey: string;
    selectedCardTitle?: string;
    title: string;
}

const PoweredBySection: React.FC<PoweredBySectionProps> = ({
    productKey,
    selectedCardTitle,
    title
}) => {
    const Icon = productKeyAttributes[productKey]?.icon;
    const LegalNotice = productKeyAttributes[productKey]?.legalNotice;
    const productName = isProductKey(productKey)
        ? PRODUCT_NAMES[productKey]
        : undefined;
    const isMobile = useIsMobile(768);

    return (
        <div css={poweredByContainerStyle}>
            <span css={poweredByTextStyle}>
                <FormattedMessage {...messages.poweredByText} />
            </span>
            <div css={poweredByRowStyle}>
                <div css={poweredByIconStyle}>
                    {Icon && (
                        <Icon
                            appearance="brand"
                            size={isMobile ? "xsmall" : "small"}
                            shouldUseNewLogoDesign
                        />
                    )}
                </div>
                {productName && (
                    <span css={poweredByTextStyle}>{productName}</span>
                )}
            </div>
            {selectedCardTitle === title && LegalNotice && productName && (
                <LegalNotice productName={productName} />
            )}
        </div>
    );
};

const Card: React.FC<CardProps> = ({
    imageSrc,
    mobileImgSrc,
    title,
    subtext,
    setSelectedTemplate,
    templateType,
    templateId,
    setSelectedTemplateId,
    setSelectedCardTitle,
    selectedCardTitle,
    teamType,
    templateMetadata,
    setSelectedTemplateMetadata,
    info,
    productKey
}) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();

    const [isInfoOpen, setIsInfoOpen] = useState(false);

    const isJsmEnabled = isServiceManagementVariation();

    const popupTrigger = useCallback(
        (triggerProps: TriggerProps) => (
            <IconButton
                {...triggerProps}
                isSelected={isInfoOpen}
                appearance="subtle"
                icon={InformationCircleIcon}
                label="Info"
                onClick={(e) => {
                    e.stopPropagation();
                    setIsInfoOpen(!isInfoOpen);
                }}
                isTooltipDisabled
            />
        ),
        [isInfoOpen]
    );

    return (
        <button
            css={[
                cardBorderStyle,
                selectedCardTitle === title
                    ? selectedButtonStyle
                    : unselectedButtonStyle
            ]}
            onClick={() => {
                createAnalyticsEvent({
                    eventType: "ui",
                    action: "clicked",
                    actionSubject: "button",
                    actionSubjectId:
                        TRACKING_EVENT_ACTION_SUBJECT_IDS.templateChoiceCard,
                    attributes: {
                        atlasTicket: "ATLAS-61649",
                        teamType,
                        templateType,
                        templateId,
                        templateName: title
                    }
                }).fire();

                setSelectedTemplate(templateType);
                setSelectedTemplateId(templateId);
                setSelectedCardTitle(title);
                setSelectedTemplateMetadata(templateMetadata);
            }}
        >
            <div css={desktopImageContainer}>
                {/* eslint-disable-next-line no-restricted-syntax */}
                <img src={imageSrc} alt={title} css={imgStyle} />
            </div>
            {/* eslint-disable-next-line no-restricted-syntax */}
            <img src={mobileImgSrc} alt={title} css={mobileImgStyle} />
            <div css={textContainerStyle}>
                <div css={titleContainerStyle}>
                    <h3 css={cardTitleStyle}>{title}</h3>
                    {isJsmEnabled && info && (
                        <Popup
                            isOpen={isInfoOpen}
                            onClose={() => setIsInfoOpen(false)}
                            placement="bottom-start"
                            zIndex={510}
                            content={() => (
                                <div css={popupContentStyle}>
                                    <div css={popupTitleStyle}>
                                        {info.title}
                                    </div>
                                    <div css={popupTextStyle}>
                                        {info.content}
                                    </div>
                                </div>
                            )}
                            trigger={popupTrigger}
                        />
                    )}
                </div>
                <p css={subtextStyle}>{subtext}</p>
                {isJsmEnabled && productKey && (
                    <PoweredBySection
                        productKey={productKey}
                        selectedCardTitle={selectedCardTitle}
                        title={title}
                    />
                )}
            </div>
        </button>
    );
};

export default Card;
